/**
 * AddByUrlModal — Ajouter un article dans le dressing à partir d'une URL produit
 *
 * Supporte toutes les boutiques mode (Zara, Sézane, Sandro, Nike, Chanel, Dior,
 * Louis Vuitton, La Redoute, etc.) + tout site qui respecte OpenGraph / JSON-LD.
 *
 * Flow :
 *  1. User collez une URL produit (ex: sezane.com/fr/product/123, nike.com/...)
 *  2. Tap "Rechercher" → Lambda scrape la page, extrait image/nom/prix/marque
 *  3. Preview affichée → user valide la catégorie / modifie si besoin
 *  4. Tap "Ajouter au dressing" → crée l'article dans la garde-robe
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, StyleSheet, ActivityIndicator, Image, ScrollView, Alert, KeyboardAvoidingView, Platform, Keyboard, TouchableWithoutFeedback,
} from 'react-native';
import AppModal from './AppModal';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { colors, spacing } from '../theme';
import { AWS_CONFIG } from '../config/aws_config';
import { useAuth } from '../context/AuthContext';
import { getPartnerName } from '../data/partners';
import { useI18n } from '../i18n';

// Canonical category values (stored in DB as-is). UI label resolved via i18n.
const CATEGORIES = ['Hauts', 'Bas', 'Robes', 'Maillots', 'Vestes', 'Manteaux', 'Chaussures', 'Sacs', 'Bijoux', 'Accessoires'];
const CATEGORY_LABEL_KEY = {
  Hauts: 'cat_tops', Bas: 'cat_bottoms', Robes: 'cat_dresses', Maillots: 'cat_swimwear',
  Vestes: 'cat_jackets', Manteaux: 'cat_coats', Chaussures: 'cat_shoes',
  Sacs: 'cat_bags', Accessoires: 'cat_accessories',
};

// v1.12.75 — Sites connus pour bloquer le scraping serveur (anti-bot Akamai/DataDome/Cloudflare).
// Pour ces hostnames, on bascule directement sur Méthode 2 (URL image) sans
// tenter la Méthode 1 (qui retournerait 403 et frustrerait l'user).
const KNOWN_BLOCKED_HOSTS = [
  'zara.com', 'zara.net',
  'hermes.com', 'hermes.fr',
  'chanel.com',
  'dior.com',
  'louisvuitton.com', 'louisvuitton.fr',
  'gucci.com', 'prada.com',
  'celine.com', 'givenchy.com',
  'fendi.com', 'burberry.com',
  'valentino.com', 'versace.com',
  'balenciaga.com', 'bottegaveneta.com',
  'loewe.com', 'miumiu.com',
  'saint-laurent.com',
];

// v1.12.181 — Facebook et Instagram redirigent les requêtes non-authentifiées
// vers la page de login → impossible d'extraire l'image. On affiche un message
// dédié et bascule sur Méthode 2 (URL image directe).
function detectSocialMediaHost(rawUrl) {
  try {
    const host = new URL(rawUrl).hostname.toLowerCase();
    if (host.includes('facebook.com') || host.includes('fb.com') || host.includes('fb.watch')) return 'facebook';
    if (host.includes('instagram.com') || host.includes('instagr.am')) return 'instagram';
    return null;
  } catch {
    return null;
  }
}

function detectBlockedHost(rawUrl) {
  try {
    const host = new URL(rawUrl).hostname.toLowerCase();
    return KNOWN_BLOCKED_HOSTS.some(d => host.includes(d));
  } catch {
    return false;
  }
}

// Hint placeholder selon le partenaire détecté
function getImagePlaceholderForUrl(rawUrl) {
  try {
    const host = new URL(rawUrl).hostname.toLowerCase();
    if (host.includes('zara'))         return 'https://static.zara.net/photos/...';
    if (host.includes('hermes'))       return 'https://assets.hermes.com/...';
    if (host.includes('chanel'))       return 'https://www.chanel.com/...';
    if (host.includes('louisvuitton')) return 'https://eu.louisvuitton.com/...';
    if (host.includes('gucci'))        return 'https://media.gucci.com/...';
  } catch {}
  return 'https://...';
}

export default function AddByUrlModal({ visible, onClose, onAdd, onSwitchToPhoto }) {
  const { user, getValidToken } = useAuth();
  const { t } = useI18n();
  const [url,      setUrl]      = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [product,  setProduct]  = useState(null);
  const [category, setCategory] = useState('Hauts');
  // v1.12.89 — pseudo optionnel (alias user)
  const [pseudo,   setPseudo]   = useState('');
  // ═══ v1.8.1 : détection de doublon dans le dressing ═══
  const [duplicateMatch, setDuplicateMatch] = useState(null);
  // ═══ v1.12.73 : fallback URL image directe (anti-bot bypass Zara/Cloudflare) ═══
  const [imageUrl, setImageUrl]               = useState('');
  const [showImageFallback, setShowImageFallback] = useState(false);
  const [imageLoading, setImageLoading]       = useState(false);

  const resetState = () => {
    setUrl('');
    setLoading(false);
    setError(null);
    setProduct(null);
    setCategory('Hauts');
    setPseudo('');
    setDuplicateMatch(null);
    setImageUrl('');
    setShowImageFallback(false);
    setImageLoading(false);
  };

  // ═══ v1.8.1 : check des doublons quand le produit est scrapé ═══
  // Compare via partner_sku (URL contient souvent l'ID) ou brand+nom normalisé.
  useEffect(() => {
    if (!product) {
      setDuplicateMatch(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const uid = user?.id || user?.sub;
        if (!uid) return;
        let wardrobe = [];
        try {
          const { getWardrobe } = require('../services/DatabaseService');
          const w = await getWardrobe(uid, user?.accessToken);
          if (Array.isArray(w)) wardrobe = w;
        } catch {}
        if (wardrobe.length === 0) {
          try {
            const cached = await AsyncStorage.getItem(`outfit_wardrobe_cache_${uid}`);
            if (cached) {
              const parsed = JSON.parse(cached);
              if (parsed?.items && Array.isArray(parsed.items)) wardrobe = parsed.items;
            }
          } catch {}
        }
        if (cancelled) return;

        const productSku = product.zalandoSku || product.zaraId || product.skimId || product.id || '';
        const productNameNorm = String(product.name || '').toLowerCase().trim();
        const productBrandNorm = String(product.brand || '').toLowerCase().trim();

        const match = wardrobe.find(item => {
          if (productSku && item?.partner_sku && String(item.partner_sku) === String(productSku)) return true;
          const itemNameNorm = String(item?.name || '').toLowerCase().trim();
          const itemBrandNorm = String(item?.brand || '').toLowerCase().trim();
          if (productNameNorm && productBrandNorm && itemNameNorm && itemBrandNorm) {
            if (itemNameNorm === productNameNorm && itemBrandNorm === productBrandNorm) return true;
          }
          return false;
        });

        if (match && !cancelled) {
          setDuplicateMatch(match);
          console.log('[AddByUrl] ⚠ doublon détecté:', match.name);
        } else if (!cancelled) {
          setDuplicateMatch(null);
        }
      } catch (e) {
        console.warn('[AddByUrl] check duplicate failed:', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [product?.id, product?.name, product?.brand, user?.id]);

  const close = () => {
    resetState();
    onClose?.();
  };

  const handleScrape = async () => {
    const trimmed = (url || '').trim();
    if (!trimmed) {
      setError(t('paste_url_error'));
      return;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
      setError(t('url_must_http'));
      return;
    }

    // v1.12.75 — Si site connu bloqué (Zara, luxes), skip le scrape et bascule
    // direct sur Méthode 2. Évite l'attente du 403 et clarifie le flow.
    if (detectBlockedHost(trimmed)) {
      console.log('[AddByUrl] hostname connu bloqué → bascule directe Méthode 2');
      setError(t('site_blocks_use_method2'));
      setShowImageFallback(true);
      return;
    }

    // v1.12.181 — Facebook/Instagram exigent l'authentification pour voir les
    // photos publiques. Le scraping serveur reçoit la page de login → impossible.
    // On guide vers Méthode 2 avec instructions précises.
    const socialHost = detectSocialMediaHost(trimmed);
    if (socialHost) {
      console.log(`[AddByUrl] ${socialHost} détecté → bascule Méthode 2`);
      const guide = socialHost === 'instagram'
        ? t('instagram_guide')
        : t('facebook_guide');
      setError(guide);
      setShowImageFallback(true);
      return;
    }

    setError(null);
    setLoading(true);
    setProduct(null);

    // v1.12.74 — Au lieu d'un popup intrusif, on auto-expand le champ "URL image"
    // dans le modal lui-même. L'user voit clairement l'alternative sans interruption.
    const triggerImageFallback = (msg) => {
      setError(msg || t('site_blocks_paste_image_below'));
      setShowImageFallback(true);
    };

    try {
      let token = null;
      try { token = typeof getValidToken === 'function' ? await getValidToken() : null; } catch {}
      if (!token) token = user?.accessToken || null;

      const res = await fetch(`${AWS_CONFIG.apiUrl}/ai/trending-products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ action: 'scrape-url', url: trimmed }),
      });

      if (!res.ok) {
        console.warn(`[AddByUrl] HTTP ${res.status}`);
        triggerImageFallback(t('site_unavailable_paste_image'));
        return;
      }

      const data = await res.json();
      if (!data.ok) {
        console.warn('[AddByUrl] data.ok=false:', data.error);
        triggerImageFallback();
        return;
      }

      // ═══ Site bloqué → on accepte le résultat partiel mais on avertit l'user ═══
      if (data.blocked) {
        console.log('[AddByUrl] site blocked, partial data:', data);
        setProduct(data);
        setCategory(data.category || 'Hauts');
        // Hint discret en gris (pas en rouge) pour signaler de compléter prix/photo
        setError(data.hint || t('site_blocks_complete_manually'));
        return;
      }

      // ═══ Validation : détecter les scrapes quasi-vides ═══
      const nameEmpty = !data.name || data.name === 'Article sans nom' || data.name.length < 3;
      const imageMissing = !data.image;
      if (nameEmpty && imageMissing) {
        console.warn('[AddByUrl] scrape vide (nom + image manquants)');
        triggerImageFallback();
        return;
      }

      setProduct(data);
      setCategory(data.category || 'Hauts');
      console.log('[AddByUrl] scraped:', data.name, data.price + '€', data.partner);
    } catch (e) {
      console.warn('[AddByUrl] scrape error:', e.message);
      triggerImageFallback(t('network_error_paste_image'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (!product) return;
    // ═══ v1.8.2 : si doublon détecté, demander confirmation explicite ═══
    if (duplicateMatch) {
      const existingName = duplicateMatch.name || duplicateMatch.brand || t('similar_item');
      const sizeInfo = duplicateMatch.size ? ` ${t('size_paren').replace('{size}', duplicateMatch.size)}` : '';
      Alert.alert(
        t('item_already_in_wardrobe'),
        `${t('you_already_have')}\n\n${existingName}${sizeInfo}\n\n${t('add_anyway_question')}`,
        [
          { text: t('cancel'), style: 'cancel' },
          {
            text: t('add_anyway'),
            style: 'default',
            onPress: () => proceedWithAdd(),
          },
        ],
        { cancelable: true }
      );
      return;
    }
    proceedWithAdd();
  };

  // v1.12.73 — Fallback : ajout direct via URL image (bypass anti-bot)
  // L'utilisateur copie l'URL de l'IMAGE depuis le site (clic droit → copier
  // image URL). Notre Lambda download cette image avec User-Agent réaliste +
  // Claude Vision pour générer ai_metadata, puis insert dans le dressing.
  const handleAddViaImageUrl = async () => {
    const trimmedImage = (imageUrl || '').trim();
    const trimmedUrl   = (url || '').trim();
    if (!trimmedImage) {
      setError(t('paste_image_url'));
      return;
    }
    if (!/^https?:\/\//i.test(trimmedImage)) {
      setError(t('image_url_must_http'));
      return;
    }

    setError(null);
    setImageLoading(true);

    try {
      let token = null;
      try { token = typeof getValidToken === 'function' ? await getValidToken() : null; } catch {}
      if (!token) token = user?.accessToken || null;

      const res = await fetch(`${AWS_CONFIG.apiUrl}/wardrobe/from-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          url: trimmedUrl || trimmedImage,
          source: 'url',
          image_url: trimmedImage,
          category, // catégorie choisie par l'user (sinon IA décide)
        }),
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        console.warn(`[AddByUrl] /wardrobe/from-url HTTP ${res.status}: ${errText.slice(0, 200)}`);
        // v1.13.56 — GA : erreur d'ajout au dressing par URL (422 non-vêtement,
        // 502 marque protégée, 401 session, quota, etc.)
        try { require('../services/AnalyticsService').trackEvent('wardrobe_add_error', { reason: 'url_http_error', source: 'url', status: res.status }); } catch {}
        // v1.12.565 — Rejet IA si isClothing=false
        if (res.status === 422) {
          let detail = '';
          try { detail = JSON.parse(errText)?.userMessage || ''; } catch {}
          Alert.alert(
            t('not_clothing_title') || 'Pas un vêtement',
            detail || t('not_clothing_msg') || 'L\'IA n\'a pas détecté de vêtement sur cette photo. Veuillez utiliser une image d\'un vêtement, chaussure, sac ou accessoire mode.',
            [{ text: 'OK' }]
          );
          setError(detail || 'Image rejetée par l\'IA');
          return;
        }
        if (res.status === 502) {
          // v1.12.83 — Image bloquée par anti-bot (Louis Vuitton, Hermès, etc.).
          // Le seul fallback fiable = save image to Photos puis ajouter par photo.
          Alert.alert(
            t('brand_protected_title'),
            t('brand_protected_message'),
            [
              { text: t('understood'), style: 'cancel' },
              {
                text: t('add_by_photo'),
                onPress: () => {
                  onClose?.();
                  setTimeout(() => onSwitchToPhoto?.(), 300);
                },
              },
            ]
          );
          setError(t('brand_protected_save_image'));
        } else if (res.status === 401) {
          setError(t('session_expired'));
        } else {
          setError(t('error_status_retry').replace('{status}', String(res.status)));
        }
        return;
      }

      const item = await res.json();
      console.log(`[AddByUrl] ✅ added via image URL: id=${item.id} cat=${item.category}`);
      // v1.13.18 — GA4 : article ajouté au dressing via URL/image
      try { require('../services/AnalyticsService').trackEvent('wardrobe_item_added', { source: 'url', category: item?.category || 'unknown' }); } catch {}

      // v1.12.565 — Si l'IA a corrigé la catégorie, prévenir l'user
      if (item._categoryOverridden && item._userCategory && item._aiCategory) {
        const CAT_KEY = {
          Hauts:'cat_tops', Bas:'cat_bottoms', Robes:'cat_dresses',
          Vestes:'cat_jackets', Manteaux:'cat_coats', Chaussures:'cat_shoes',
          Sacs:'cat_bags', Accessoires:'cat_accessories', Bijoux:'cat_jewelry',
          Maillots:'cat_swimwear',
        };
        const tr = (c) => (CAT_KEY[c] && t(CAT_KEY[c])) || c;
        const msg = (t('category_corrected_msg') || 'L\'IA a corrigé la catégorie : "{user}" → "{ai}". L\'article est ajouté avec la bonne catégorie.')
          .replace('{user}', tr(item._userCategory))
          .replace('{ai}', tr(item._aiCategory));
        Alert.alert(
          t('category_corrected_title') || 'Catégorie corrigée',
          msg,
          [{ text: 'OK' }]
        );
      }

      // Signal au parent que l'item est DÉJÀ inséré → wardrobe refresh
      onAdd?.({ _alreadyAdded: true, _itemId: item.id, ...item });
      resetState();
    } catch (e) {
      console.warn('[AddByUrl] image-url error:', e.message);
      setError(t('network_error_retry'));
    } finally {
      setImageLoading(false);
    }
  };

  const proceedWithAdd = () => {
    if (!product) return;

    const newItem = {
      name:         product.name,
      pseudo:       (pseudo || '').trim() || null, // v1.12.89
      category:     category || 'Hauts',
      brand:        product.brand || '',
      image:        product.image || null,
      image_url:    product.image || null,
      price:        product.price || 0,
      url:          product.url || url,
      partner:      product.partner || 'other',
      color:        '',
      source:       'add-by-url',
    };

    onAdd?.(newItem);
    resetState();
  };

  return (
    <AppModal
      visible={visible}
      animationType="slide"
      transparent
      statusBarTranslucent
      onRequestClose={close}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={s.overlay}
        keyboardVerticalOffset={0}
      >
        {/* Tap en dehors du sheet → ferme le clavier (natif) ou ferme le modal (web)
            v1.12.119 — sur web, l'utilisateur ne pouvait pas fermer le popup en
            cliquant à côté ; seul le bouton ✕ marchait, et il était parfois
            difficile à cliquer. Désormais, tap backdrop = close sur web. */}
        <TouchableWithoutFeedback
          onPress={Platform.OS === 'web' ? close : Keyboard.dismiss}
          accessible={false}
        >
          <View style={s.backdrop} />
        </TouchableWithoutFeedback>

        <View style={s.sheet}>
          <ScrollView
            contentContainerStyle={{ paddingBottom: 30 }}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            {/* Header */}
            <View style={s.header}>
              <Text style={s.title}>{t('add_from_url_title')}</Text>
              <TouchableOpacity
                onPress={close}
                hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
                style={{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center', borderRadius: 20, backgroundColor: '#F5EFE6' }}
                accessibilityLabel={t('close')}
              >
                <Text style={s.close}>✕</Text>
              </TouchableOpacity>
            </View>

            <Text style={s.subtitle}>
              {t('add_url_subtitle')}
            </Text>

            {/* Hint box */}
            <View style={s.hint}>
              <Text style={s.hintText}>
                {t('add_url_hint_main')}{'\n\n'}
                <Text style={{ fontWeight: '600' }}>{t('add_url_hint_social_label')}</Text>{t('add_url_hint_social_text')} <Text style={{ fontWeight: '600' }}>{t('add_url_hint_method2_label')}</Text> {t('add_url_hint_method2_field')}
              </Text>
            </View>

            {/* Input URL */}
            <View style={s.inputWrap}>
              <TextInput
                style={s.input}
                placeholder={t('paste_link_here')}
                placeholderTextColor="#999"
                value={url}
                onChangeText={setUrl}
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="url"
                spellCheck={false}
                contextMenuHidden={false}
                importantForAutofill="yes"
                keyboardType="url"
                editable={!loading}
                returnKeyType="search"
                onSubmitEditing={handleScrape}
                blurOnSubmit={true}
                multiline={false}
              />
              {!!url && !loading && (
                <TouchableOpacity onPress={() => setUrl('')} style={s.clearBtn}>
                  <Text style={s.clearText}>✕</Text>
                </TouchableOpacity>
              )}
            </View>

            {!product && (
              <TouchableOpacity
                onPress={handleScrape}
                disabled={loading || !url.trim()}
                style={[s.scrapeBtn, (loading || !url.trim()) && s.btnDisabled]}
                activeOpacity={0.8}
              >
                {loading ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={s.scrapeBtnText}>🔍 {t('search_button')}</Text>
                )}
              </TouchableOpacity>
            )}

            {/* v1.12.74 — Message d'erreur seulement si on n'est PAS dans le flow image */}
            {error && !showImageFallback && <Text style={s.error}>💡 {error}</Text>}

            {/* v1.12.74 — Lien discret pour activer manuellement le mode image */}
            {!product && !showImageFallback && (
              <TouchableOpacity
                onPress={() => setShowImageFallback(true)}
                style={{ paddingVertical: 10, alignItems: 'center', marginTop: 4 }}
              >
                <Text style={{ fontSize: 13, color: '#8B6F2E', textDecorationLine: 'underline' }}>
                  {t('site_blocked_paste_image_link')}
                </Text>
              </TouchableOpacity>
            )}

            {/* ═══ MÉTHODE 2 : URL de l'image directe (bypass anti-bot Zara/etc.) ═══ */}
            {showImageFallback && !product && (
              <View style={s.method2Box}>
                {/* Header avec badge "Méthode 2" + bouton fermer */}
                <View style={s.method2Header}>
                  <View style={s.method2Badge}>
                    <Text style={s.method2BadgeText}>{t('method2_badge')}</Text>
                  </View>
                  <Text style={s.method2Title}>{t('image_url_label')}</Text>
                  <TouchableOpacity onPress={() => { setShowImageFallback(false); setError(null); }} hitSlop={{top:10,bottom:10,left:10,right:10}}>
                    <Text style={{ fontSize: 18, color: '#999' }}>✕</Text>
                  </TouchableOpacity>
                </View>

                {/* Si le scrape a échoué, on l'indique en haut */}
                {error && (
                  <View style={s.errorBox}>
                    <Text style={s.errorBoxText}>⚠️ {error}</Text>
                  </View>
                )}

                {/* Instructions étape par étape — adaptées web/natif */}
                {Platform.OS === 'web' ? (
                  <View style={s.stepsBox}>
                    <Text style={s.stepLine}><Text style={s.stepNum}>1.</Text> {t('url_step_web_1')}</Text>
                    <Text style={s.stepLine}><Text style={s.stepNum}>2.</Text> {t('url_step_web_2')}</Text>
                    <Text style={s.stepLine}><Text style={s.stepNum}>3.</Text> {t('url_step_web_3')}</Text>
                    <Text style={s.stepLine}><Text style={s.stepNum}>4.</Text> {t('url_step_web_4')}</Text>
                  </View>
                ) : (
                  <View style={s.stepsBox}>
                    <Text style={s.stepLine}><Text style={s.stepNum}>1.</Text> {t('url_step_native_1')}</Text>
                    <Text style={s.stepLine}><Text style={s.stepNum}>2.</Text> {t('url_step_native_2')}</Text>
                    <Text style={s.stepLine}><Text style={s.stepNum}>3.</Text> {t('url_step_native_3')}</Text>
                    <Text style={s.stepLine}><Text style={s.stepNum}>4.</Text> {t('url_step_native_4')}</Text>
                  </View>
                )}

                {/* Input URL image */}
                <View style={s.inputWrap}>
                  <TextInput
                    style={s.input}
                    placeholder={getImagePlaceholderForUrl(url)}
                    placeholderTextColor="#999"
                    value={imageUrl}
                    onChangeText={setImageUrl}
                    autoCapitalize="none"
                    autoCorrect={false}
                    keyboardType="url"
                    editable={!imageLoading}
                    multiline={false}
                  />
                  {!!imageUrl && !imageLoading && (
                    <TouchableOpacity onPress={() => setImageUrl('')} style={s.clearBtn}>
                      <Text style={s.clearText}>✕</Text>
                    </TouchableOpacity>
                  )}
                </View>

                {/* Catégorie */}
                <Text style={[s.previewLabel, { marginTop: 14 }]}>{t('category_caps')}</Text>
                <View style={s.categoriesRow}>
                  {CATEGORIES.map(cat => (
                    <TouchableOpacity
                      key={cat}
                      onPress={() => setCategory(cat)}
                      style={[s.catChip, category === cat && s.catChipActive]}
                      activeOpacity={0.7}
                    >
                      <Text style={[s.catChipText, category === cat && s.catChipTextActive]}>
                        {t(CATEGORY_LABEL_KEY[cat] || '') || cat}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                <TouchableOpacity
                  onPress={handleAddViaImageUrl}
                  disabled={imageLoading || !imageUrl.trim()}
                  style={[s.addBtn, (imageLoading || !imageUrl.trim()) && s.btnDisabled, { marginTop: 16 }]}
                  activeOpacity={0.85}
                >
                  {imageLoading ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <Text style={s.addBtnText}>🖼️ {t('add_via_image_button')}</Text>
                  )}
                </TouchableOpacity>

                {/* Lien fallback photo */}
                <TouchableOpacity
                  onPress={() => {
                    onClose?.();
                    setTimeout(() => onSwitchToPhoto?.(), 300);
                  }}
                  style={{ paddingVertical: 12, alignItems: 'center' }}
                >
                  <Text style={{ fontSize: 12, color: '#666', textDecorationLine: 'underline' }}>
                    {t('or_add_by_photo')}
                  </Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Preview */}
            {product && (
              <View style={s.preview}>
                <Text style={s.previewLabel}>{t('preview_caps')}</Text>
                <View style={s.card}>
                  {product.image ? (
                    <Image source={{ uri: product.image }} style={s.img} resizeMode="cover" />
                  ) : (
                    <View style={[s.img, { alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F3F0' }]}>
                      <Text style={{ fontSize: 40 }}>👗</Text>
                    </View>
                  )}
                  <View style={s.cardInfo}>
                    {!!product.brand && <Text style={s.brand}>{product.brand.toUpperCase()}</Text>}
                    <Text style={s.name} numberOfLines={2}>{product.name}</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      {product.price > 0 && <Text style={s.price}>{product.price} €</Text>}
                      {product.partner && (
                        <View style={s.partnerChip}>
                          <Text style={s.partnerText}>{getPartnerName(product.partner).toUpperCase()}</Text>
                        </View>
                      )}
                    </View>
                  </View>
                </View>

                {/* Pseudo (optionnel) — v1.12.89 */}
                <Text style={[s.previewLabel, { marginTop: 18 }]}>{t('pseudo_optional_caps')}</Text>
                <TextInput
                  style={{
                    borderWidth: 1, borderColor: '#E5E5E5',
                    borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10,
                    fontSize: 13, color: '#111', marginTop: 8,
                    backgroundColor: '#FAFAFA',
                  }}
                  placeholder={t('pseudo_placeholder')}
                  placeholderTextColor="#999"
                  value={pseudo}
                  onChangeText={setPseudo}
                  autoCorrect={true}
                  autoCapitalize="sentences"
                  maxLength={60}
                />

                {/* Catégorie éditable */}
                <Text style={[s.previewLabel, { marginTop: 18 }]}>{t('category_caps')}</Text>
                <View style={s.categoriesRow}>
                  {CATEGORIES.map(cat => (
                    <TouchableOpacity
                      key={cat}
                      onPress={() => setCategory(cat)}
                      style={[s.catChip, category === cat && s.catChipActive]}
                      activeOpacity={0.7}
                    >
                      <Text style={[s.catChipText, category === cat && s.catChipTextActive]}>
                        {t(CATEGORY_LABEL_KEY[cat] || '') || cat}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                {/* Actions */}
                <View style={{ gap: 10, marginTop: 20 }}>
                  <TouchableOpacity
                    onPress={handleAdd}
                    style={s.addBtn}
                    activeOpacity={0.85}
                  >
                    <Text style={s.addBtnText}>✅ {t('add_to_wardrobe_button')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => { setProduct(null); setError(null); }}
                    style={s.retryBtn}
                    activeOpacity={0.7}
                  >
                    <Text style={s.retryBtnText}>{t('try_another_url')}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </AppModal>
  );
}

const s = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'transparent',
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject, // couvre toute la zone pour capter les taps
  },
  sheet: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 24, borderTopRightRadius: 24,
    paddingHorizontal: 20, paddingTop: 18,
    maxHeight: '92%',
  },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 4,
  },
  title: { fontSize: 20, fontWeight: '700', color: '#111', letterSpacing: 0.2 },
  close: { fontSize: 22, color: '#666', padding: 4 },
  subtitle: { fontSize: 13, color: '#666', marginBottom: 18, lineHeight: 18 },

  hint: {
    backgroundColor: '#FEF3C7',
    borderWidth: 1, borderColor: '#FCD34D',
    padding: 12, borderRadius: 10,
    marginBottom: 16,
  },
  hintText: { fontSize: 12, color: '#78350F', lineHeight: 17 },

  inputWrap: {
    flexDirection: 'row', alignItems: 'center',
    borderWidth: 1, borderColor: '#E5E5E5',
    borderRadius: 12, paddingHorizontal: 14,
    backgroundColor: '#FAFAFA',
    marginBottom: 12,
  },
  input: {
    flex: 1, paddingVertical: 14,
    fontSize: 14, color: '#111',
  },
  clearBtn: { padding: 6 },
  clearText: { fontSize: 16, color: '#999' },

  scrapeBtn: {
    backgroundColor: '#111',
    paddingVertical: 15,
    borderRadius: 100,
    alignItems: 'center',
    marginBottom: 10,
  },
  scrapeBtnText: { color: '#fff', fontSize: 13, fontWeight: '700', letterSpacing: 1 },
  btnDisabled: { opacity: 0.4 },

  // v1.10.87 : style "hint" discret (gris) au lieu de rouge intimidant.
  // Les vrais échecs de scraping affichent désormais un Alert popup
  // proposant l'ajout par photo. Cette zone ne contient plus que :
  //   - hints "URL invalide / vide" (validation locale)
  //   - hint "site bloqué" (scraping partiel réussi mais incomplet)
  error: {
    color: colors.text.muted, fontSize: 12,
    marginTop: 8, textAlign: 'center', fontStyle: 'italic',
  },

  preview: { marginTop: 20 },
  previewLabel: {
    fontSize: 10, letterSpacing: 2, color: '#999',
    fontWeight: '700', marginBottom: 10,
  },
  card: {
    flexDirection: 'row',
    backgroundColor: '#F9F7F3',
    borderRadius: 14,
    overflow: 'hidden',
    borderWidth: 1, borderColor: '#EDEBE8',
  },
  img: { width: 110, height: 140 },
  cardInfo: { flex: 1, padding: 14, justifyContent: 'center', gap: 5 },
  brand: { fontSize: 10, color: '#999', letterSpacing: 1.5, fontWeight: '700' },
  name: { fontSize: 13, fontWeight: '600', color: '#111', lineHeight: 18 },
  price: { fontSize: 15, fontWeight: '700', color: '#111' },
  partnerChip: {
    backgroundColor: '#2A7D4F', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4,
  },
  partnerText: { fontSize: 9, color: '#fff', fontWeight: '700', letterSpacing: 0.5 },

  categoriesRow: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 8,
  },
  catChip: {
    paddingHorizontal: 14, paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1, borderColor: '#E5E5E5',
    backgroundColor: '#fff',
  },
  catChipActive: {
    backgroundColor: '#111', borderColor: '#111',
  },
  catChipText: { fontSize: 12, color: '#333', fontWeight: '500' },
  catChipTextActive: { color: '#fff', fontWeight: '600' },

  addBtn: {
    backgroundColor: '#2A7D4F',
    paddingVertical: 15, borderRadius: 100,
    alignItems: 'center',
  },
  addBtnText: { color: '#fff', fontSize: 13, fontWeight: '700', letterSpacing: 1 },

  retryBtn: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  retryBtnText: { color: '#666', fontSize: 12, textDecorationLine: 'underline' },

  // ═══ v1.12.74 — Méthode 2 (URL image directe) ═══
  method2Box: {
    marginTop: 16,
    paddingTop: 18, paddingHorizontal: 14, paddingBottom: 14,
    backgroundColor: '#FAF7F1',
    borderRadius: 14,
    borderWidth: 1, borderColor: '#E5DFD3',
  },
  method2Header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 14,
  },
  method2Badge: {
    backgroundColor: '#8B6F2E',
    paddingHorizontal: 8, paddingVertical: 3,
    borderRadius: 6,
  },
  method2BadgeText: { color: '#fff', fontSize: 9, fontWeight: '700', letterSpacing: 0.8 },
  method2Title: { flex: 1, fontSize: 14, fontWeight: '700', color: '#3A2F1A' },

  errorBox: {
    backgroundColor: '#FEF3C7',
    borderRadius: 8,
    padding: 10,
    marginBottom: 12,
  },
  errorBoxText: { fontSize: 12, color: '#78350F', lineHeight: 17 },

  stepsBox: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 12,
    marginBottom: 14,
    borderWidth: 1, borderColor: '#EDE7DA',
  },
  stepLine: { fontSize: 12, color: '#5A4D3A', marginBottom: 6, lineHeight: 17 },
  stepNum: { fontWeight: '700', color: '#8B6F2E' },
});
